import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, X, ChevronRight, Snowflake } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';

interface StreakStatus {
  currentStreak: number;
  isActiveToday: boolean;
  streakAtRisk: boolean;
  freezesAvailable: number;
  isFreezeActive: boolean;
}

export default function StreakRiskBanner() {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const { preferences } = usePreferences();
  const [streakStatus, setStreakStatus] = useState<StreakStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && preferences.streak_reminder_enabled) {
      loadStreakStatus();
    } else {
      setLoading(false);
    }
  }, [user, preferences.streak_reminder_enabled]);

  async function loadStreakStatus() {
    try {
      const data = await fetchApi<{ streak: StreakStatus }>('/api/streaks/me');
      setStreakStatus(data.streak);
    } catch (error) {
      console.error('Failed to load streak status:', error);
    } finally {
      setLoading(false);
    }
  }

  // Don't show if:
  // - Loading
  // - User dismissed it
  // - Streak reminders disabled
  // - No streak data
  // - User has already been active today
  // - Streak is not at risk
  // - Freeze is already active
  if (
    loading ||
    dismissed ||
    !preferences.streak_reminder_enabled ||
    !streakStatus ||
    streakStatus.isActiveToday ||
    !streakStatus.streakAtRisk ||
    streakStatus.isFreezeActive
  ) {
    return null;
  }

  const hasFreeze = streakStatus.freezesAvailable > 0;

  return (
    <div className="mb-6 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white mb-1">
            Your {streakStatus.currentStreak}-day streak is at risk!
          </h3>
          <p className="text-gray-300 text-sm mb-3">
            Complete a lesson today to keep your streak alive.
            {hasFreeze && (
              <span className="text-blue-400">
                {' '}Or use a streak freeze if you need a break.
              </span>
            )}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Continue Learning
              <ChevronRight className="w-4 h-4" />
            </Link>

            {hasFreeze && (
              <span className="inline-flex items-center gap-1.5 text-sm text-blue-400">
                <Snowflake className="w-4 h-4" />
                {streakStatus.freezesAvailable} freeze{streakStatus.freezesAvailable !== 1 ? 's' : ''} available
              </span>
            )}
          </div>
        </div>

        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
